import { useEffect, useState } from "react";
import LOGO from "../assets/fl.png";
import styled, { keyframes, css } from "styled-components";

const Loading = () => {
  const [progress, setProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [isFading, setIsFading] = useState(false);

  useEffect(() => {
    const startTime = Date.now();

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      /* Ease-out curve: fast at first, slows near 100 */
      const raw = elapsed / 3000;
      const eased = 1 - Math.pow(1 - Math.min(raw, 1), 2.4);
      setProgress(Math.min(100, Math.round(eased * 100)));
    }, 50);

    const timeout = setTimeout(() => {
      setProgress(100);
      clearInterval(interval);
      /* Small pause at 100% before fading out */
      setTimeout(() => {
        setIsFading(true);
        setTimeout(() => setIsVisible(false), 700);
      }, 320);
    }, 3000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  if (!isVisible) return null;

  return (
    <Overlay $fading={isFading} role="status" aria-label="Loading Felicie Auto">

      {/* Decorative layered background */}
      <BgLayer1 />
      <BgLayer2 />
      <DiagonalAccent />

      {/* Corner marks — engineering feel */}
      <CornerTL aria-hidden="true" />
      <CornerBR aria-hidden="true" />

      <Center>
        {/* Logo */}
        <LogoWrap>
          <LogoGlow />
          <Logo fetchpriority="high" src={LOGO} alt="Felicie Auto" />
        </LogoWrap>

        {/* Brand name */}
        <BrandRow aria-hidden="true">
          <BrandLine />
          <BrandText>FELICIE AUTO SPARE PARTS</BrandText>
          <BrandLine />
        </BrandRow>

        {/* Progress bar */}
        <BarSection>
          <BarMeta>
            <BarLabel>Loading</BarLabel>
            <BarPercent>{progress}%</BarPercent>
          </BarMeta>
          <BarTrack>
            <BarFill style={{ width: `${progress}%` }} />
            <BarShine style={{ width: `${progress}%` }} />
          </BarTrack>
        </BarSection>

        {/* Tagline */}
        <Tagline aria-hidden="true">
          Hyundai &amp; Kia Specialists · Kigali, Rwanda
        </Tagline>
      </Center>
    </Overlay>
  );
};

export default Loading;

/* ═══════════════════════════════════════
   KEYFRAMES
═══════════════════════════════════════ */

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to   { opacity: 0; }
`;

const logoReveal = keyframes`
  0%   { opacity: 0; transform: scale(0.88) translateY(10px); filter: brightness(0.6); }
  60%  { opacity: 1; transform: scale(1.04) translateY(0);    filter: brightness(1.1); }
  100% { opacity: 1; transform: scale(1)    translateY(0);    filter: brightness(1); }
`;

const glowPulse = keyframes`
  0%, 100% { opacity: 0.55; transform: scale(1); }
  50%       { opacity: 0.85; transform: scale(1.12); }
`;

const barShimmer = keyframes`
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(400%); }
`;

const scanLine = keyframes`
  0%   { transform: translateX(-100%); opacity: 0; }
  10%  { opacity: 1; }
  90%  { opacity: 1; }
  100% { transform: translateX(100vw); opacity: 0; }
`;

const diagonalSlide = keyframes`
  from { transform: translateX(-60px) skewX(-18deg); opacity: 0; }
  to   { transform: translateX(0)     skewX(-18deg); opacity: 1; }
`;

/* ═══════════════════════════════════════
   STYLES
═══════════════════════════════════════ */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: #071116;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  ${(p) =>
    p.$fading &&
    css`
      animation: ${fadeOut} 0.7s ease forwards;
    `}

  /* Subtle scan line effect */
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 3px;
    height: 100%;
    background: linear-gradient(
      to bottom,
      transparent,
      rgba(0, 180, 216, 0.6) 40%,
      rgba(0, 180, 216, 0.6) 60%,
      transparent
    );
    animation: ${scanLine} 3s ease-in-out 0.4s both;
    pointer-events: none;
  }
`;

/* Background depth layers */
const BgLayer1 = styled.div`
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 70% 60% at 50% 40%, rgba(0, 119, 182, 0.14) 0%, transparent 65%),
    radial-gradient(ellipse 40% 30% at 80% 80%, rgba(227, 34, 43, 0.07) 0%, transparent 60%);
  pointer-events: none;
`;

const BgLayer2 = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.045;
  background-image:
    linear-gradient(rgba(0,180,216,0.8) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0,180,216,0.8) 1px, transparent 1px);
  background-size: 48px 48px;
  pointer-events: none;
`;

/* Red diagonal accent strip — automotive energy */
const DiagonalAccent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 360px;
  height: 6px;
  background: linear-gradient(90deg, #e3222b, rgba(227, 34, 43, 0));
  transform: skewX(-18deg);
  transform-origin: left bottom;
  animation: ${diagonalSlide} 0.9s 0.3s cubic-bezier(0.22, 1, 0.36, 1) both;
`;

/* Engineering corner marks */
const cornerBase = css`
  position: absolute;
  width: 28px;
  height: 28px;
  opacity: 0.3;
`;

const CornerTL = styled.div`
  ${cornerBase}
  top: 28px;
  left: 28px;
  border-top: 2px solid #00b4d8;
  border-left: 2px solid #00b4d8;
  animation: ${fadeIn} 0.5s 0.6s ease both;

  @media (max-width: 480px) { top: 16px; left: 16px; }
`;

const CornerBR = styled.div`
  ${cornerBase}
  bottom: 28px;
  right: 28px;
  border-bottom: 2px solid #00b4d8;
  border-right: 2px solid #00b4d8;
  animation: ${fadeIn} 0.5s 0.6s ease both;

  @media (max-width: 480px) { bottom: 16px; right: 16px; }
`;

/* Center content */
const Center = styled.div`
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  width: 100%;
  max-width: 440px;
  padding: 0 32px;
`;

/* Logo */
const LogoWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 28px;
`;

const LogoGlow = styled.div`
  position: absolute;
  width: 220px;
  height: 220px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(0, 180, 216, 0.22) 0%, transparent 70%);
  animation: ${glowPulse} 2.4s ease-in-out infinite;
  pointer-events: none;
`;

const Logo = styled.img`
  position: relative;
  width: 180px;
  max-width: 52vw;
  object-fit: contain;
  filter: drop-shadow(0 4px 28px rgba(0, 180, 216, 0.3));
  animation: ${logoReveal} 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
`;

/* Brand name row */
const BrandRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  margin-bottom: 40px;
  animation: ${fadeIn} 0.5s 0.5s ease both;
`;

const BrandLine = styled.div`
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(0, 180, 216, 0.4), transparent);
`;

const BrandText = styled.span`
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(0, 180, 216, 0.7);
  white-space: nowrap;

  @media (max-width: 380px) { font-size: 8px; letter-spacing: 0.12em; }
`;

/* Progress bar */
const BarSection = styled.div`
  width: 100%;
  animation: ${fadeIn} 0.5s 0.7s ease both;
`;

const BarMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 10px;
`;

const BarLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.3);
`;

const BarPercent = styled.span`
  font-size: 13px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.06em;
  color: #00b4d8;
  font-family: 'Courier New', monospace;
`;

const BarTrack = styled.div`
  position: relative;
  height: 3px;
  background: rgba(255, 255, 255, 0.07);
  border-radius: 999px;
  overflow: hidden;
`;

const BarFill = styled.div`
  position: absolute;
  inset: 0;
  height: 100%;
  background: linear-gradient(90deg, #0077b6 0%, #00b4d8 60%, #48cae4 100%);
  border-radius: 999px;
  transition: width 0.15s ease-out;
  transform-origin: left;
`;

/* Shimmer sweep on top of the fill */
const BarShine = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  border-radius: 999px;
  overflow: hidden;
  transition: width 0.15s ease-out;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.55) 50%,
      transparent 100%
    );
    width: 40%;
    animation: ${barShimmer} 1.4s ease-in-out infinite;
  }
`;

/* Tagline */
const Tagline = styled.p`
  margin-top: 20px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.2);
  text-align: center;
  animation: ${fadeIn} 0.5s 1s ease both;
`;